import React from "react";

interface DataPoint {
  label: string;
  value: number;
}

interface BarChartProps {
  data: DataPoint[];
  height?: number;
  barColor?: string;
  labelColor?: string;
  valueColor?: string;
  title?: string;
}

export const BarChart = ({
  data,
  height = 200,
  barColor = "#735fe2",
  labelColor = "#666",
  valueColor = "#333",
  title,
}: BarChartProps) => {
  if (!data || data.length === 0) {
    return <div className="text-center p-4">No data available</div>;
  }

  const maxValue = Math.max(...data.map((d) => d.value));

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-medium mb-2 text-gray-700">{title}</h3>
      )}
      <div
        className="w-full bg-white/20 rounded-lg p-4 border border-white/30"
        style={{ height: `${height}px` }}
      >
        <div className="flex h-full items-end justify-between gap-2">
          {data.map((item, index) => {
            const percentage = (item.value / maxValue) * 100;
            return (
              <div
                key={index}
                className="flex flex-col items-center justify-end h-full"
                style={{ width: `${100 / data.length}%` }}
              >
                <div
                  className="text-xs font-medium mb-1"
                  style={{ color: valueColor }}
                >
                  {item.value}
                </div>
                <div
                  className="w-full rounded-t-sm transition-all duration-500 ease-in-out"
                  style={{
                    height: `${Math.max(percentage, 2)}%`,
                    backgroundColor: barColor,
                    minHeight: "4px",
                  }}
                ></div>
                <div
                  className="text-xs mt-1 truncate w-full text-center"
                  style={{ color: labelColor }}
                >
                  {item.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface LineChartProps {
  data: DataPoint[];
  height?: number;
  lineColor?: string;
  fillColor?: string;
  labelColor?: string;
  valueColor?: string;
  title?: string;
}

export const LineChart = ({
  data,
  height = 200,
  lineColor = "#735fe2",
  fillColor = "rgba(115, 95, 226, 0.1)",
  labelColor = "#666",
  valueColor = "#333",
  title,
}: LineChartProps) => {
  if (!data || data.length === 0) {
    return <div className="text-center p-4">No data available</div>;
  }

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const range = maxValue - minValue;

  // Generate SVG path for the line
  const generateLinePath = () => {
    const width = 100;
    const chartHeight = height - 40; // Adjust for labels
    const segmentWidth = width / (data.length - 1);

    return data
      .map((point, i) => {
        const x = i * segmentWidth;
        const normalizedValue =
          range === 0 ? 0.5 : (point.value - minValue) / range;
        const y = chartHeight - normalizedValue * chartHeight;
        return `${i === 0 ? "M" : "L"}${x},${y}`;
      })
      .join(" ");
  };

  // Generate SVG path for the area under the line
  const generateAreaPath = () => {
    const width = 100;
    const chartHeight = height - 40;
    const segmentWidth = width / (data.length - 1);

    let path = data
      .map((point, i) => {
        const x = i * segmentWidth;
        const normalizedValue =
          range === 0 ? 0.5 : (point.value - minValue) / range;
        const y = chartHeight - normalizedValue * chartHeight;
        return `${i === 0 ? "M" : "L"}${x},${y}`;
      })
      .join(" ");

    // Complete the path by drawing to the bottom right, then bottom left, then back to start
    const lastX = (data.length - 1) * segmentWidth;
    path += ` L${lastX},${chartHeight} L0,${chartHeight} Z`;
    return path;
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-medium mb-2 text-gray-700">{title}</h3>
      )}
      <div
        className="w-full bg-white/20 rounded-lg p-4 border border-white/30"
        style={{ height: `${height}px` }}
      >
        <svg
          viewBox={`0 0 100 ${height - 40}`}
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          <path d={generateAreaPath()} fill={fillColor} strokeWidth="0" />
          <path
            d={generateLinePath()}
            fill="none"
            stroke={lineColor}
            strokeWidth="1"
          />
          {data.map((point, i) => {
            const width = 100;
            const chartHeight = height - 40;
            const segmentWidth = width / (data.length - 1);
            const x = i * segmentWidth;
            const normalizedValue =
              range === 0 ? 0.5 : (point.value - minValue) / range;
            const y = chartHeight - normalizedValue * chartHeight;

            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="1.5"
                fill="white"
                stroke={lineColor}
                strokeWidth="1"
              />
            );
          })}
        </svg>
        <div className="flex justify-between mt-2">
          {data.map((item, index) => (
            <div
              key={index}
              className="text-xs truncate text-center"
              style={{ color: labelColor, width: `${100 / data.length}%` }}
            >
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
